import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { IPublicDesktopV2OffersGetOffersResponse } from 'shared/repositories/commercial-search-offers/desktop/v2/offers/get-offers';
import { FetchGetOffersLoadService } from 'shared/store/serverSide/commercial-search-offers/desktop/v2/offers/get-offers/load';
import { CardApiContext } from 'shared/types/CardApiContext';
import { CardContextData } from 'shared/types/CardContextData';

import { useRefetchClusters } from './internal/hooks/useRefetchClusters';
import { InitCardApiContext } from './types';

export function useInitCardApi(): InitCardApiContext {
  const queryClient = useQueryClient();
  const fetchGetOffersLoadService = useMemo(() => new FetchGetOffersLoadService(queryClient), [queryClient]);

  const refetchClustersOnSuccess = useRefetchClusters();

  return useCallback<(cardContextData: CardContextData) => CardApiContext>(
    cardContextData => ({
      addToFavorite: {
        onError(_error, _variables, context: IPublicDesktopV2OffersGetOffersResponse | null): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onMutate(): IPublicDesktopV2OffersGetOffersResponse | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          if (prev) {
            const next: IPublicDesktopV2OffersGetOffersResponse = JSON.parse(JSON.stringify(prev));
            next.offers[cardContextData.index].data.extendedProperties.isFavorite = true;

            fetchGetOffersLoadService.setQueryData(next);
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      deleteFromFavorite: {
        onError(_error, _variables, context): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onMutate(): IPublicDesktopV2OffersGetOffersResponse | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          if (prev) {
            const next: IPublicDesktopV2OffersGetOffersResponse = JSON.parse(JSON.stringify(prev));
            next.offers[cardContextData.index].data.extendedProperties.isFavorite = false;

            fetchGetOffersLoadService.setQueryData(next);
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
    }),
    [fetchGetOffersLoadService, refetchClustersOnSuccess],
  );
}
